/**
 * MartialProficiency (Foundry: rule-element/martial-proficiency.ts): a custom attack or defense
 * proficiency whose `definition` predicate says which weapons or armour it covers, with a fixed
 * rank (`value`) or a rank linked to a category (`sameAs`, capped by `maxRank`). Applied in the
 * applyAEs phase at priority 9, before ActiveEffectLikes, so those may adjust it.
 */
import type { MartialProficiencyData } from "../actor-data";
import { isValidPredicate, type RawPredicate } from "../predicate";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import { sluggify } from "../slug";
import type { EngineState } from "../state";
import { ARMOR_CATEGORIES, PROFICIENCY_RANKS, WEAPON_CATEGORIES, type ProficiencyRankName, type Rank } from "../values";
import type { RuleHandler, RuleInstance } from "./base";

export interface MartialProficiencyRuleData {
  kind: "attack" | "defense";
  definition: RawPredicate;
  sameAs: string | null;
  maxRank: Exclude<ProficiencyRankName, "untrained"> | null;
  value: unknown;
  visible: boolean;
}

const CATEGORIES: readonly string[] = [...WEAPON_CATEGORIES, ...ARMOR_CATEGORIES];
const MAX_RANKS: readonly string[] = PROFICIENCY_RANKS.slice(1);

function parse(rule: RuleInstance, state: EngineState): void {
  const s = rule.source;
  const kind = s.kind === undefined ? "attack" : s.kind;
  if (kind !== "attack" && kind !== "defense") return rule.fail(`kind: "${String(s.kind)}" is not "attack" or "defense"`);
  if (!isValidPredicate(s.definition)) return rule.fail("definition: must be a predicate");
  if (s.sameAs !== undefined && !(typeof s.sameAs === "string" && CATEGORIES.includes(s.sameAs))) return rule.fail(`sameAs: "${String(s.sameAs)}" is not a weapon or armor category`);
  if (s.maxRank !== undefined && !(typeof s.maxRank === "string" && MAX_RANKS.includes(s.maxRank))) return rule.fail(`maxRank: "${String(s.maxRank)}" is not a proficiency rank`);
  if (s.visible !== undefined && typeof s.visible !== "boolean") return rule.fail("visible: must be a boolean");
  // Labels are localisation keys, some with an injected selection ("...SimpleMartial.{item|flags...}").
  const label = resolveInjectedProperties(rule, state, rule.label, { warn: false });
  if (!rule.ignored) rule.label = state.catalogue?.localize(label) ?? label;
  rule.slug ??= sluggify(rule.label);
  const data: MartialProficiencyRuleData = {
    kind,
    definition: structuredClone(s.definition),
    sameAs: typeof s.sameAs === "string" ? s.sameAs : null,
    maxRank: typeof s.maxRank === "string" ? (s.maxRank as MartialProficiencyRuleData["maxRank"]) : null,
    value: cleanRuleValue(s.value),
    visible: s.visible !== false,
  };
  rule.data = data as unknown as Record<string, unknown>;
}

function onApplyActiveEffects(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored || !rule.test(state)) return;
  const data = rule.data as unknown as MartialProficiencyRuleData;
  const resolved = data.value === undefined ? 1 : Number(resolveValue(rule, state, data.value, 1)) || 1;
  if (rule.ignored) return;
  const rank = Math.min(4, Math.max(1, Math.trunc(resolved))) as Rank;
  const definition = resolveInjectedProperties(rule, state, structuredClone(data.definition));
  if (rule.ignored) return;
  const slug = rule.slug ?? sluggify(rule.label);
  const key = data.kind === "attack" ? "attacks" : "defenses";
  const record: MartialProficiencyData = {
    definition,
    label: rule.label,
    rank,
    value: 0,
    breakdown: "",
    visible: data.visible,
    source: rule.item.uuid,
    sourceName: rule.item.name,
    custom: true,
  };
  if (data.sameAs) record.sameAs = data.sameAs;
  if (data.maxRank) record.maxRank = data.maxRank;
  state.actor.system.proficiencies[key][slug] = record;
}

export const MartialProficiencyHandler: RuleHandler = { key: "MartialProficiency", priority: () => 9, parse, onApplyActiveEffects };
